import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export const CountdownTimer: React.FC = () => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const weddingDate = new Date("2026-09-12T16:00:00");
      const difference = +weddingDate - +new Date();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);

  const TimeUnit: React.FC<{ value: number; label: string }> = ({
    value,
    label,
  }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: { xs: 0.5, sm: 0.75, md: 1 },
        minWidth: { xs: "60px", sm: "70px", md: "100px" },
        flex: { xs: "1 1 auto", sm: "0 0 auto" },
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontFamily: "'Great Vibes', cursive",
          fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2.5rem" },
          color: "white",
          mb: 0.5,
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Raleway', sans-serif",
          fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.8rem" },
          color: "white",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          textAlign: "center",
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        position: "relative",
        py: { xs: 2, md: 2.5 },
        px: { xs: 2, sm: 3, md: 5 },
        width: { xs: "calc(100% - 32px)", sm: "fit-content" },
        maxWidth: { xs: "100%", sm: "none" },
        mx: "auto",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-52%, -48%) rotate(-2deg)",
          width: "102%",
          height: "108%",
          backgroundColor: "primary.main",
          opacity: 0.7,
          zIndex: -2,
          borderRadius: "4px",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-48%, -52%) rotate(1deg)",
          width: "102%",
          height: "108%",
          backgroundColor: "primary.main",
          opacity: 0.9,
          zIndex: -1,
          borderRadius: "4px",
        },
      }}
    >
      <Typography
        variant="h2"
        sx={{
          textAlign: "center",
          mb: { xs: 3, md: 4 },
          fontFamily: "'Great Vibes', cursive",
          fontSize: { xs: "2rem", sm: "2.5rem", md: "4rem" },
          color: "white",
          textShadow: "1px 1px 2px rgba(0,0,0,0.2)",
          lineHeight: { xs: 1.2, md: 1.1 },
        }}
      >
        {t("sections.countdown.title")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: { xs: 1.5, sm: 2.5, md: 4 },
          flexWrap: "wrap",
        }}
      >
        <TimeUnit value={timeLeft.days} label={t("countdown.days")} />
        <TimeUnit value={timeLeft.hours} label={t("countdown.hours")} />
        <TimeUnit value={timeLeft.minutes} label={t("countdown.minutes")} />
        <TimeUnit value={timeLeft.seconds} label={t("countdown.seconds")} />
      </Box>
    </Box>
  );
};
